import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, updateProfile } from "firebase/auth";
import { auth } from "../../firebase";
import { API_ENDPOINTS, API_BASE } from "../../config/api";
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  BadgeDollarSign,
  TrendingUp,
  ArrowRight,
  AlertTriangle,
  Gift,
  CheckCircle,
} from "lucide-react";
import "./Register.css";

export default function Register() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ Validate referral code (supports up to 20 characters)
  const validateReferral = async (code) => {
    if (!code || code.length < 13 || code.length > 20) {
      setReferralValid(null);
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.VALIDATE_REFERRAL(code));
      const data = await response.json();
      
      if (data.success && data.valid) {
        setReferralValid(true);
        setReferralInfo(data.referrer);
      } else {
        setReferralValid(false);
        setReferralInfo(null);
      }
    } catch (error) {
      console.error("Referral validation error:", error);
      setReferralValid(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (errors[name] || errors.formSummary) {
      setErrors((prev) => ({ ...prev, [name]: "", formSummary: "" }));
    }
  };

  // ✅ Handle manual referral code input (copy/paste)
  const handleReferralChange = (e) => {
    const code = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    setReferralCode(code);
    setReferralValid(null);
    setReferralInfo(null);
    
    // Validate when code reaches minimum length (13) or max (20)
    if (code.length >= 13 && code.length <= 20) {
      validateReferral(code);
    }
  };

  const validate = () => {
    let newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email)) newErrors.email = "Invalid email address";
    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 8) newErrors.password = "Password must be at least 8 characters";
    if (!formData.confirmPassword) newErrors.confirmPassword = "Please confirm your password";
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (referralCode && referralValid === false) newErrors.referralCode = "Invalid referral code";

    return newErrors;
  };

  const resetForm = () => {
    setFormData({ fullName: "", email: "", password: "", confirmPassword: "" });
    setErrors({});
    setReferralCode("");
    setReferralValid(null);
    setReferralInfo(null);
    setShowPassword(false);
    setShowConfirm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const cleanEmail = formData.email.trim().toLowerCase().replace(/\s+/g, "");
      const userCredential = await createUserWithEmailAndPassword(auth, cleanEmail, formData.password);
      await updateProfile(userCredential.user, { displayName: formData.fullName.trim() });

      // ✅ Send referralCode to backend (supports up to 20 chars)
      const backendResponse = await fetch(`${API_ENDPOINTS.REGISTER}?t=${Date.now()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName.trim(),
          email: cleanEmail,
          password: formData.password,
          referralCode: referralCode && referralValid ? referralCode : null
        }),
      });

      const backendData = await backendResponse.json();

      if (!backendResponse.ok) {
        if (backendData.code === "EMAIL_EXISTS" || backendData.code === "DUPLICATE_KEY" || backendResponse.status === 400) {
          try { await userCredential.user.delete(); } catch (err) {}
          throw new Error(backendData.message || "This email is already registered.");
        }
        throw new Error(backendData.message || "Failed to register in backend.");
      }

      localStorage.setItem("userEmail", cleanEmail);
      if (backendData.user?.referralCode) {
        localStorage.setItem("userReferralCode", backendData.user.referralCode);
      }
      
      resetForm();
      navigate("/otp", { state: { email: cleanEmail } });
      
    } catch (error) {
      let formException = {};
      if (error.code) {
        switch (error.code) {
          case "auth/email-already-in-use":
            formException.formSummary = "This email is already registered.";
            formException.email = "Account already exists";
            break;
          case "auth/invalid-email":
            formException.email = "Invalid email format.";
            break;
          case "auth/weak-password":
            formException.password = "Password must be at least 8 characters.";
            break;
          default:
            formException.formSummary = error.message;
        }
      } else if (error.message) {
        if (error.message.includes("already registered") || error.message.includes("exists")) {
          formException.formSummary = "This email is already registered.";
          formException.email = "Account already exists";
        } else {
          formException.formSummary = error.message;
        }
      } else {
        formException.formSummary = "Registration failed. Please try again.";
      }
      setErrors(formException);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="register-page">
      <div className="background-glow glow-left"></div>
      <div className="background-glow glow-right"></div>

      <header className="register-header">
        <div className="logo-row">
          <div className="logo-icon live-logo-animation">
            <div className="bar bar1"></div>
            <div className="bar bar2"></div>
            <div className="bar bar3"></div>
            <TrendingUp size={16} className="trend-icon" />
          </div>
          <h1 className="logo-text">Era<span>X</span></h1>
        </div>
      </header>

      <main className="register-container">
        <section className="form-card">
          <div className="top-highlight"></div>
          
          <div className="top-section">
            <div className="profile-circle"><User size={32} strokeWidth={1.8} /></div>
            <div>
              <h2>ACCOUNT DETAILS</h2>
              <p>This data initializes your private <span>custody investment environment</span>.</p>
            </div>
          </div>

          <div className="form-divider" style={{ marginTop: '20px' }}>
            <div className="line"></div>
            <span>PROVISION IDENTITY DETAILS</span>
            <div className="line"></div>
          </div>

          {errors.formSummary && (
            <div className="form-alert-error-banner" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)', borderRadius: '8px', color: '#ef4444', fontSize: '0.82rem', marginBottom: '16px', fontFamily: 'monospace' }}>
              <AlertTriangle size={16} style={{ flexShrink: 0 }} />
              <span>{errors.formSummary}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="input-group">
              <label>Full Name</label>
              <div className={`input-box ${errors.fullName ? "input-error" : ""}`}>
                <User size={18} className="input-icon" />
                <input type="text" name="fullName" disabled={isSubmitting} placeholder="Enter your full name" value={formData.fullName} onChange={handleChange} autoComplete="name" />
              </div>
              {errors.fullName && <p className="error-text">⚠️ {errors.fullName}</p>}
            </div>

            <div className="input-group">
              <label>Email</label>
              <div className={`input-box ${errors.email ? "input-error" : ""}`}>
                <Mail size={18} className="input-icon" />
                <input type="email" name="email" disabled={isSubmitting} placeholder="Enter your security email" value={formData.email} onChange={handleChange} autoComplete="email" />
              </div>
              {errors.email && <p className="error-text">⚠️ {errors.email}</p>}
            </div>

            <div className="input-group">
              <label>Password</label>
              <div className={`input-box ${errors.password ? "input-error" : ""}`}>
                <Lock size={18} className="input-icon" />
                <input type={showPassword ? "text" : "password"} name="password" disabled={isSubmitting} placeholder="Create strong password" value={formData.password} onChange={handleChange} autoComplete="new-password" />
                <button type="button" className="eye-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="error-text">⚠️ {errors.password}</p>}
            </div>

            <div className="input-group">
              <label>Confirm Password</label>
              <div className={`input-box ${errors.confirmPassword ? "input-error" : ""}`}>
                <Lock size={18} className="input-icon" />
                <input type={showConfirm ? "text" : "password"} name="confirmPassword" disabled={isSubmitting} placeholder="Repeat master password" value={formData.confirmPassword} onChange={handleChange} autoComplete="new-password" />
                <button type="button" className="eye-toggle" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && <p className="error-text">⚠️ {errors.confirmPassword}</p>}
            </div>

            {/* ✅ REFERRAL CODE INPUT - MAX 20 CHARACTERS */}
            <div className="input-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Gift size={16} style={{ color: '#f3ba2f' }} />
                Referral Code / EraX ID (Optional)
              </label>
              <div className={`input-box ${errors.referralCode ? "input-error" : ""} ${referralValid ? 'input-success' : ''}`}>
                <Gift size={18} className="input-icon" />
                <input
                  type="text"
                  name="referralCode"
                  disabled={isSubmitting}
                  placeholder="Paste your referral code here"
                  value={referralCode}
                  onChange={handleReferralChange}
                  maxLength={20}  // ✅ UPDATED: Max 20 characters
                  style={{ textTransform: 'uppercase', letterSpacing: '1px' }}
                />
                {referralValid === true && (
                  <CheckCircle size={18} style={{ color: '#10b981', marginLeft: '8px' }} />
                )}
              </div>
              
              {referralValid === true && referralInfo && (
                <p className="success-text" style={{ color: '#10b981', fontSize: '0.85rem', marginTop: '6px' }}>
                  ✅ Valid! Referred by {referralInfo.name}
                </p>
              )}
              
              {referralValid === false && referralCode.length >= 13 && (
                <p className="error-text">⚠️ Invalid referral code or EraX ID</p>
              )}
              
              {errors.referralCode && <p className="error-text">⚠️ {errors.referralCode}</p>}
              
              <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '4px' }}>
                Enter or paste your referral code (13-20 characters)
              </p>
            </div>

            <button 
              type="submit" 
              className="register-btn shimmer-action-btn"
              disabled={isSubmitting}
              style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
            >
              <span>{isSubmitting ? "Provisioning..." : "Sign Up"}</span>
              <ArrowRight className="arrow-right-shimmer" size={18} strokeWidth={2.5} />
            </button>

            <div className="terms-container">
              <ShieldCheck size={16} className="terms-icon" />
              <p>By standing up this account node, you certify full clearance agreement with our <a href="/">Terms of Service</a> and <a href="/">Privacy Regulations</a>.</p>
            </div>
          </form>

          <div className="features-rack">
            <div className="feat-node">
              <ShieldCheck className="feat-icon" size={20} />
              <div><h5>Secure Cluster</h5><p>Isolated Endpoints</p></div>
            </div>
            <div className="feat-rack-divider"></div>
            <div className="feat-node">
              <Lock className="feat-icon" size={20} />
              <div><h5>Vault Custody</h5><p>AES-256 Protection</p></div>
            </div>
            <div className="feat-rack-divider"></div>
            <div className="feat-node">
              <BadgeDollarSign className="feat-icon" size={20} />
              <div><h5>Zero Spread</h5><p>Transparent Ledgers</p></div>
            </div>
          </div>
        </section>

        <section className="graphic-side">
          <div className="orbit-ring ring1"></div>
          <div className="orbit-ring ring2"></div>
          <div className="chart-card-premium">
            <div className="card-top-header">
              <div className="pulse-indicator"></div>
              <span>Live EraX Allocation Data Tracker</span>
            </div>
            <div className="bars-matrix">
              <div className="bar-item h1"></div>
              <div className="bar-item h2"></div>
              <div className="bar-item h3"></div>
              <div className="bar-item h4"></div>
              <div className="bar-item h5"></div>
              <div className="bar-item h6"></div>
            </div>
            <div className="growth-line-sweep"></div>
            <div className="crypto-coin floating-coin-1"><span>$</span></div>
            <div className="crypto-coin floating-coin-2"><span>$</span></div>
            <div className="crypto-coin floating-coin-3"><span>$</span></div>
          </div>
        </section>
      </main>
    </div>
  );
}